import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-form';
import PropTypes, { any } from 'prop-types';
import moment from 'moment-timezone';

import { getApiAxiosInstance } from '../../../utils/network';
import NextAvailableSidePanel from './NextAvailableSidePanel';

import styles from './nextAvailable.scss';
import { getAvailableDrawsToAddForDay } from '../../../api/tmsEventsNextAvailableApi';
import SelectableDrawList from './SelectableDrawList';
import { getModifiedDrawsListForAddDrawPanel, updateSelectableStatus } from '../../../utils/nextAvailable';
import { createTypeByIdDict, updateCategoryTypesName } from '../../../utils/formatting';
import { addDrawsToFacilityAndDayConfig } from '../../../actions/next-available-actions';

const AddDrawsSidePanel = (props) => {
    const {
        className,
        closeSidePanel,
        eventId,
        eventSchedule,
    } = props;

    const dispatch = useDispatch();
    const nextAvailable = useSelector(({ nextAvailable }) => nextAvailable);
    const coreUI = useSelector(({ coreUI }) => coreUI);

    const { drawCategoryTypes = [] } = coreUI.types?.tms?.draw || {};
    const drawCategoryTypesDict = updateCategoryTypesName(createTypeByIdDict(drawCategoryTypes));

    const dateOfConfig = moment(nextAvailable?.selectedDate).tz(eventSchedule?.timeZone?.value).format('MMM DD');

    const [data, setData] = useState(null);

    const defaultValues = useMemo(() => {
        if (data) {
            return {
                datedDraws: updateSelectableStatus(data?.datedDraws),
                unDatedDraws: updateSelectableStatus(data?.unDatedDraws),
                previouslyStartedDraws: updateSelectableStatus(data?.previouslyStartedDraws),
                changed: false,
            };
        }

        return null;
    }, [data, eventSchedule]);

    useEffect(() => {
        const getData = async () => {
            const availableDrawsResponse = await getAvailableDrawsToAddForDay(
                getApiAxiosInstance(),
                eventId,
                nextAvailable?.selectedDateCourtConfig?.id,
                nextAvailable?.selectedFacilityId,
            );
            const modifiedDraws = getModifiedDrawsListForAddDrawPanel(availableDrawsResponse.data, drawCategoryTypesDict, nextAvailable, eventSchedule);
            setData(modifiedDraws);
        };

        if (!data) {
            getData();
        }
    }, [data, eventId, eventSchedule]);

    const onFormSubmit = async (values) => {
        const { datedDraws, unDatedDraws, previouslyStartedDraws } = values;
        const drawIds = [];
        [...datedDraws, ...unDatedDraws, ...previouslyStartedDraws].forEach((draw) => {
            if (draw.childDraws?.length > 0) {
                draw.childDraws.forEach((childDraw) => {
                    if (childDraw.isSelected) {
                        drawIds.push(childDraw.id);
                    }
                });
            } else if (draw.isSelected) {
                drawIds.push(draw.id);
            } else {
                // Do nothing added to resolve sonar warning
            }
        });
        dispatch(addDrawsToFacilityAndDayConfig(eventId, drawIds));
    };

    const onCancelClick = () => {
        closeSidePanel();
    };

    if (!defaultValues) {
        return null;
    }

    return (
        <Form
            onSubmit={onFormSubmit}
            defaultValues={defaultValues}
        >
            {(formApi) => {
                const { submitForm, values } = formApi;

                const isAtleastOneDrawSelected = values.datedDraws.some((draw) => draw.isSelected) ||
                    values.unDatedDraws.some((draw) => draw.isSelected) ||
                    values.previouslyStartedDraws.some((draw) => draw.isSelected);

                return (
                    <form onSubmit={submitForm} id="dayConfigForm">
                        <NextAvailableSidePanel
                            className={className}
                            closeSideBar={closeSidePanel}
                        >
                            <div className={styles.header} data-testid="next-available.add-draws.title">
                                Select draws
                            </div>
                            <div className={styles.content}>
                                <p className={`mb0 ${styles.notice}`} data-testid="next-available.add-draws.notice">
                                    Please select which draws you’d like to add. if you do not see a draw appearing, please check the assigned date in edit draws.
                                </p>
                                {data?.datedDraws?.length > 0 && (
                                    <SelectableDrawList
                                        field="datedDraws"
                                        title={`${dateOfConfig} draws`}
                                        draws={values.datedDraws}
                                        formApi={formApi}
                                    />
                                )}
                                {data?.unDatedDraws?.length > 0 && (
                                    <SelectableDrawList
                                        field="unDatedDraws"
                                        title="Undated draws"
                                        draws={values.unDatedDraws}
                                        formApi={formApi}
                                    />
                                )}
                                {data?.previouslyStartedDraws?.length > 0 && (
                                    <SelectableDrawList
                                        field="previouslyStartedDraws"
                                        title="Started draws with unfinished matches"
                                        draws={values.previouslyStartedDraws}
                                        formApi={formApi}
                                    />
                                )}
                            </div>
                            <div className={`${styles.footer} d-flex align-items-center`}>
                                <button
                                    className={`btn btn-default ${styles.submitButton} mr8 flex-1`}
                                    type="button"
                                    onClick={onCancelClick}
                                    data-testid="next-available.add-draws.cancel-btn"
                                >
                                    Cancel
                                </button>
                                <button
                                    className={`btn btn-primary ${styles.submitButton} flex-1`}
                                    type="submit"
                                    disabled={!values.changed || !isAtleastOneDrawSelected}
                                    data-testid="next-available.add-draws.submit-btn"
                                >
                                    Add draws
                                </button>
                            </div>
                        </NextAvailableSidePanel>
                    </form>
                );
            }}
        </Form>
    );
};

AddDrawsSidePanel.propTypes = {
    className: PropTypes.string,
    closeSidePanel: PropTypes.func.isRequired,
    eventId: PropTypes.number.isRequired,
    eventSchedule: PropTypes.objectOf(any).isRequired,
};

AddDrawsSidePanel.defaultProps = {
    className: '',
};

export default AddDrawsSidePanel;
